import { useMemo } from 'react'
import { useBlogIntegrations } from 'hooks/useBlogIntegrations'
import { usePostProperties } from 'components/ArticlePostPreview/usePostProperties'
import { AssigneeType, PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { useArticlePostPreviewContext } from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { ExternalIntegrationEnum, ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { BlogMetadataRecord } from 'api/clients/BlogMetadataRecordsClient'

// Import all the individual hooks and their types
import { useBlogMetadataFormState, BlogMetadataFormState } from './useBlogMetadataFormState'
import { useBlogMetadataUpdates } from './useBlogMetadataUpdates'
import { useBlogMetadataCategories, BlogMetadataCategoriesState } from './useBlogMetadataCategories'
import { useBlogMetadataTags, BlogMetadataTagsState } from './useBlogMetadataTags'
import { useBlogMetadataImages, BlogMetadataImagesState, BlogMetadataImagesHandlers } from './useBlogMetadataImages'
import { useBlogMetadataIntegration, BlogMetadataIntegrationState } from './useBlogMetadataIntegration'

export interface BlogMetadataCompositionOptions {
  articleId: string
  initialData?: BlogMetadataRecord
}

export interface BlogMetadataCompositionReturn {
  integrationInfo: {
    blogIntegrationsCount: number
    isPostPublished: boolean
    selectedIntegration: ExternalIntegration | undefined
  }
  states: {
    formState: BlogMetadataFormState
    categoriesState: BlogMetadataCategoriesState
    tagsState: BlogMetadataTagsState
    imageState: BlogMetadataImagesState
    integrationState: BlogMetadataIntegrationState
  }
  handlers: {
    enhancedFormHandlers: {
      handleTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
      handleAuthorChange: (e: React.ChangeEvent<HTMLInputElement>) => void
      handleSummaryChange: (e: React.ChangeEvent<HTMLInputElement>) => void
      handleSlugChange: (e: React.ChangeEvent<HTMLInputElement>) => void
      handleLaunchDateChange: (date: string) => void
    }
    enhancedCategoriesHandlers: {
      handleCategoriesChange: (categories: string[]) => void
      handleCategoryDropdownClose: (selectedCats: string[], updatedAvailable: string[]) => void
    }
    enhancedTagsHandlers: {
      handleTagsChange: (tags: string[]) => void
    }
    enhancedIntegrationHandlers: {
      handleBlogSelect: (blogId: string) => void
      handleAuthorSelect: (authorId: string) => void
    }
    imageHandlers: BlogMetadataImagesHandlers
  }
  refs: {
    fileInputRef: React.RefObject<HTMLInputElement>
  }
}

/**
 * Custom hook that encapsulates all the setup logic for BlogMetadata
 * Combines multiple hooks and creates enhanced handlers with API integration
 */
export const useBlogMetadataComposition = ({
  articleId,
  initialData,
}: BlogMetadataCompositionOptions): BlogMetadataCompositionReturn => {
  // Initialize external dependencies
  const { blogIntegrations } = useBlogIntegrations()
  const { status } = usePostProperties({ id: articleId, type: AssigneeType.Article })
  const { scheduledPost } = useArticlePostPreviewContext()
  const goHighLevelEnabled = useFlipperFlag('go_high_level_integration')
  const wixEnabled = useFlipperFlag('wix_blog_integration')
  const isPostPublished = status?.value === PostStatusPropertyValue.Posted

  // Get the currently selected integration from the scheduled post
  const selectedIntegration = scheduledPost?.external_integration

  // Only pass integration if it's WordPress, GoHighLevel, or Wix and has additional_data
  const supportedIntegration =
    selectedIntegration &&
    (selectedIntegration.provider === ExternalIntegrationEnum.WordPress ||
      selectedIntegration.provider === ExternalIntegrationEnum.GoHighLevel ||
      selectedIntegration.provider === ExternalIntegrationEnum.Wix) &&
    selectedIntegration.additional_data
      ? (selectedIntegration as ExternalIntegration)
      : undefined

  // Initialize all hooks with proper destructuring
  const { formState, handlers: formHandlers } = useBlogMetadataFormState({ initialData })
  const { categoriesState, handlers: categoriesHandlers } = useBlogMetadataCategories({
    initialData,
  })
  const { tagsState, handlers: tagsHandlers } = useBlogMetadataTags({ initialData })
  const { integrationState, handlers: integrationHandlers } = useBlogMetadataIntegration({
    initialData,
    selectedIntegration: supportedIntegration,
    goHighLevelEnabled,
    wixEnabled,
  })

  // Create current state for updates hook - memoized to prevent unnecessary re-creation
  const currentState = useMemo(
    () => ({
      title: formState.title,
      author: formState.author,
      summary: formState.summary,
      slug: formState.slug,
      tags: tagsState.tags,
      selectedCategories: categoriesState.selectedCategories,
      selectedBlogId: integrationState.selectedBlogId,
      selectedAuthorId: integrationState.selectedAuthorId,
    }),
    [
      formState.title,
      formState.author,
      formState.summary,
      formState.slug,
      tagsState.tags,
      categoriesState.selectedCategories,
      integrationState.selectedBlogId,
      integrationState.selectedAuthorId,
    ],
  )

  const updateHandlers = useBlogMetadataUpdates({
    initialData,
    currentState,
    onImageStateUpdate: (updatedFields) => {
      // Handle image state updates if needed
    },
  })

  // Initialize images hook with update integration
  const {
    imageState,
    handlers: imageHandlers,
    refs,
  } = useBlogMetadataImages({
    initialData,
    onImageUpdate: (featuredMedia) => {
      updateHandlers.updateMetadata({ featured_media: featuredMedia })
    },
  })

  // Combine form handlers with update logic - memoized to prevent unnecessary re-creation
  const enhancedFormHandlers = useMemo(
    () => ({
      handleTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        formHandlers.handleTitleChange(e)
        updateHandlers.debouncedUpdateMetadata({ title: e.target.value })
      },
      handleAuthorChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        formHandlers.handleAuthorChange(e)
        updateHandlers.debouncedUpdateMetadata({ author: e.target.value })
      },
      handleSummaryChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        formHandlers.handleSummaryChange(e)
        updateHandlers.debouncedUpdateMetadata({ summary: e.target.value })
      },
      handleSlugChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        formHandlers.handleSlugChange(e)
        updateHandlers.debouncedUpdateMetadata({ url_slug: e.target.value })
      },
      handleLaunchDateChange: (date: string) => {
        formHandlers.handleLaunchDateChange(date)
        updateHandlers.debouncedUpdateMetadata({ launch_date: date })
      },
    }),
    [formHandlers, updateHandlers],
  )

  // Combine categories handlers with update logic - memoized to prevent unnecessary re-creation
  const enhancedCategoriesHandlers = useMemo(
    () => ({
      handleCategoriesChange: (categories: string[]) => {
        categoriesHandlers.handleCategoriesChange(categories)
        updateHandlers.updateMetadata({ categories })
      },
      handleCategoryDropdownClose: (selectedCats: string[], updatedAvailable: string[]) => {
        categoriesHandlers.handleCategoryDropdownClose(selectedCats, updatedAvailable)
        updateHandlers.updateMetadata({ categories: selectedCats })
      },
    }),
    [categoriesHandlers, updateHandlers],
  )

  // Combine tags handlers with update logic - memoized to prevent unnecessary re-creation
  const enhancedTagsHandlers = useMemo(
    () => ({
      handleTagsChange: (tags: string[]) => {
        tagsHandlers.handleTagsChange(tags)
        updateHandlers.updateMetadata({ tags })
      },
    }),
    [tagsHandlers, updateHandlers],
  )

  // Combine integration handlers with update logic - memoized to prevent unnecessary re-creation
  const enhancedIntegrationHandlers = useMemo(
    () => ({
      handleBlogSelect: (blogId: string) => {
        integrationHandlers.handleBlogSelect(blogId)
        updateHandlers.updateMetadata({ selected_blog_id: blogId })
      },
      handleAuthorSelect: (authorId: string) => {
        integrationHandlers.handleAuthorSelect(authorId)
        updateHandlers.updateMetadata({ selected_author_id: authorId })
      },
    }),
    [integrationHandlers, updateHandlers],
  )

  return {
    integrationInfo: {
      blogIntegrationsCount: blogIntegrations.length,
      isPostPublished,
      selectedIntegration,
    },
    states: {
      formState,
      categoriesState,
      tagsState,
      imageState,
      integrationState,
    },
    handlers: {
      enhancedFormHandlers,
      enhancedCategoriesHandlers,
      enhancedTagsHandlers,
      enhancedIntegrationHandlers,
      imageHandlers,
    },
    refs,
  }
}
